import styled from 'styled-components';

import { useMemo } from 'react';
import Input from '@paradigm/design-system/src/components/Input';
import {
  RfqBuilderLegProvider,
  useRfqBuilderLegView,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import {
  blueMana,
  darkerBlackPearl,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { useSubmitted } from '@paradigm/features/src/forms/submitted-context';
import { Divider as DefaultDivider } from '#/components/UnifiedRfqs/drfq/common';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import TransparentSelectField from '#/components/UnifiedRfqs/components/TransparentSelectField';
import { usePositionsView } from '@paradigm/features/src/positions/contexts/store';
import { Position } from '@paradigm/features/src/positions/entities/positions';
import { Maybe } from '@paradigm/utils/types';
import { getProduct } from '@paradigm/features/src/products/domain';
import StrategyDetailsBuyerTooltip from '../../components/StrategyDetailsBuyerTooltip';
import ActionSelector from './ActionSelector';
import {
  FutureLegExpirationSelector,
  OptionLegExpirationSelector,
} from './ExpirationSelector';
import StrikeSelector from './StrikeSelector';
import ProductSelector from './ProductSelector';
import InstrumentSelector from './InstrumentSelector';
import RatioField from './RatioField';
import RemoveLegButton from './RemoveLegButton';
import PriceField from './PriceField';
import LegsTableActions from './LegsTableActions';
import QuantityField from './QuantityField';
import DuplicateLegButton from './DuplicateLegButton';

export default function LegsTable() {
  const view = useRfqBuilderView();
  const isMobile = useIsMobile();

  const header = !isMobile && (
    <Header>
      <Row isOrderBook={view.isOrderBook}>
        <span>Product</span>
        <span>Action</span>
        <span>Instrument</span>
        <span>Expiry</span>
        <span>Strike</span>
        <span>Leg Price</span>
        <span>Ratio</span>
        {!view.isOrderBook && <span>Qty</span>}
        <span />
        {!view.isOrderBook && (
          <PositionBaseCell css="white-space: nowrap;">
            Open Position
          </PositionBaseCell>
        )}
      </Row>
    </Header>
  );
  return (
    <div>
      <Caption>
        {title} <LegsTableActions />
      </Caption>
      <Container>
        {header}
        <Body>
          {view.legs.map((legView, index) => (
            <RfqBuilderLegProvider key={legView.index} legView={legView}>
              <LegRow index={index} />
            </RfqBuilderLegProvider>
          ))}
        </Body>
      </Container>
    </div>
  );
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
    line-height: 1.1875rem;
  }
`;

const title = (
  <TitleContainer>
    Strategy Details (Buyer)
    <StrategyDetailsBuyerTooltip />
  </TitleContainer>
);

interface LegRowProps {
  readonly index: number;
}
function LegRow({ index }: LegRowProps) {
  const { strategy, isOrderBook } = useRfqBuilderView();
  const view = useRfqBuilderLegView();
  const { didSubmit } = useSubmitted();

  const isStrategyCustom = strategy.selectedOption?.value === null;

  let content;
  switch (view.kind) {
    case 'FUTURE':
      content = (
        <>
          <UnpaddedCell aria-label="product">
            <Label>Product</Label>
            <ProductSelector />
          </UnpaddedCell>
          <div role="cell" aria-label="action">
            <Label>Action</Label>
            <ActionSelector />
          </div>
          <div
            role="cell"
            aria-label="instrument"
            data-testid={view.instrument_id}
          >
            <Label>Instrument</Label>
            <InstrumentKind>{view.instrumentKind}</InstrumentKind>
          </div>
          <UnpaddedCell aria-label="expiry">
            <Label>Expiry</Label>
            <FutureLegExpirationSelector />
          </UnpaddedCell>
          <div role="cell" aria-label="strike">
            <Label disabled>Strike</Label>
            <UnsupportedItem />
          </div>
          {view.price.isSupported ? (
            <UnpaddedCell aria-label="price">
              <Label>Leg Price</Label>
              <PriceField />
            </UnpaddedCell>
          ) : (
            <div role="cell" aria-label="price">
              <Label disabled>Leg Price</Label>
              <UnsupportedItem />
            </div>
          )}
          <UnpaddedCell aria-label="ratio">
            <Label disabled={view.ratio.isDisabled}>Ratio</Label>
            <RatioField />
          </UnpaddedCell>
          {!isOrderBook && (
            <UnpaddedCell
              className="mobile-align-items-start"
              aria-label="quantity"
            >
              <Label>Qty</Label>
              <QuantityField />
            </UnpaddedCell>
          )}
          <Divider />
          <UnpaddedCell>
            <Label />
            <FlexContainer>
              {view.isHedge ? <HedgeBadge /> : null}
              <DuplicateLegButton />
              {view.isRemovable && <RemoveLegButton />}
            </FlexContainer>
          </UnpaddedCell>
          <PositionQtyCell />
        </>
      );
      break;
    case 'OPTION':
      content = (
        <>
          <UnpaddedCell aria-label="product">
            <Label>Product</Label>
            <ProductSelector />
          </UnpaddedCell>
          <div role="cell" aria-label="action">
            <Label>Action</Label>
            <ActionSelector />
          </div>
          <UnpaddedCell
            aria-label="instrument"
            data-testid={view.instrument_id}
          >
            <Label>Instrument</Label>
            <InstrumentSelector />
          </UnpaddedCell>
          <UnpaddedCell aria-label="expiry">
            <Label>Expiry</Label>
            <OptionLegExpirationSelector />
          </UnpaddedCell>
          <UnpaddedCell
            className="mobile-align-items-start"
            aria-label="strike"
          >
            <Label>Strike</Label>
            <StrikeSelector />
          </UnpaddedCell>
          <div role="cell" aria-label="price">
            <Label disabled>Leg Price</Label>
            <UnsupportedItem />
          </div>
          <UnpaddedCell aria-label="ratio">
            <Label disabled={view.ratio.isDisabled}>Ratio</Label>
            <RatioField />
          </UnpaddedCell>
          {!isOrderBook && (
            <UnpaddedCell
              className="mobile-align-items-start"
              aria-label="quantity"
            >
              <Label>Qty</Label>
              <QuantityFieldContainer>
                <QuantityField />
              </QuantityFieldContainer>
            </UnpaddedCell>
          )}
          <Divider />
          <UnpaddedCell>
            <Label />
            <FlexContainer>
              <DuplicateLegButton />
              {isStrategyCustom && <RemoveLegButton />}
            </FlexContainer>
          </UnpaddedCell>
          <PositionQtyCell />
        </>
      );
      break;
    case undefined:
      content = (
        <>
          <UnpaddedCell aria-label="product">
            <Label>Product</Label>
            <ProductSelector />
          </UnpaddedCell>
          <UnpaddedCell aria-label="action">
            <Label>Action</Label>
            <TransparentSelectField isDisabled invalid />
          </UnpaddedCell>
          <UnpaddedCell aria-label="instrument">
            <Label>Instrument</Label>
            <TransparentSelectField isDisabled invalid />
          </UnpaddedCell>
          <UnpaddedCell aria-label="expiry">
            <Label>Expiry</Label>
            <TransparentSelectField isDisabled invalid />
          </UnpaddedCell>
          <div role="cell" aria-label="strike">
            <Label disabled>Strike</Label>
            <UnsupportedItem />
          </div>
          <div role="cell" aria-label="price">
            <Label disabled>Leg Price</Label>
            <UnsupportedItem />
          </div>
          <UnpaddedCell aria-label="ratio">
            <Label>Ratio</Label>
            <Input
              variant="transparent"
              size="small"
              inputType="numeric"
              disabled
              warning={didSubmit}
            />
          </UnpaddedCell>
          {!isOrderBook && (
            <UnpaddedCell
              className="mobile-align-items-start"
              aria-label="quantity"
            >
              <Label>Qty</Label>
              <Input
                size="small"
                variant="transparent"
                inputType="numeric"
                disabled
                warning={didSubmit}
              />
            </UnpaddedCell>
          )}
          <Divider />
          <UnpaddedCell>
            <Label />
            <FlexContainer>
              <RemoveLegButton />
            </FlexContainer>
          </UnpaddedCell>
          <PositionQtyCell />
        </>
      );
      break;
    // no default
  }

  return (
    <Row
      isOrderBook={isOrderBook}
      data-testid="leg-row"
      role="row"
      aria-label={`leg row ${index}`}
    >
      {content}
    </Row>
  );
}

function formatPositionQuantity(position: Maybe<Position>) {
  if (position == null) return NEGATIVE_SIGN;
  const product = getProduct(position.product_code);
  return position.quantity.toFormat(product.quantityPrecision);
}

function PositionQtyCell() {
  const view = useRfqBuilderLegView();
  const builderView = useRfqBuilderView();
  const { getAccountPositionByInstrumentId } = usePositionsView();
  const instrumentPosition = useMemo(() => {
    if (builderView.account.selectedOption == null) return null;
    if (!('instrument' in view && view.instrument != null)) return null;

    return getAccountPositionByInstrumentId(
      view.instrument,
      builderView.account.selectedOption,
    );
  }, [view, getAccountPositionByInstrumentId, builderView]);

  if (builderView.isOrderBook) return null;
  if (instrumentPosition == null)
    return <PositionBaseCell>{NEGATIVE_SIGN}</PositionBaseCell>;

  return (
    <PositionBaseCell>
      <Label>Open Position</Label>
      {formatPositionQuantity(instrumentPosition)}
      <span css="opacity: 0.6;font-size: 0.6rem;">
        {instrumentPosition?.clearing_currency}
      </span>
    </PositionBaseCell>
  );
}

const UnsupportedItem = styled.span.attrs({ children: NEGATIVE_SIGN })`
  opacity: 0.3;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-right: 1rem;
  }
`;

const InstrumentKind = styled.span`
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
    padding-right: 1rem;
  }
`;

const PositionBaseCell = styled.div`
  color: white;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  padding-left: 12px;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const QuantityFieldContainer = styled.div`
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    input::placeholder {
      color: ${blueMana};
      opacity: 1;
    }
  }
`;

const UnpaddedCell = styled.div.attrs({ role: 'cell' })`
  position: relative;
  margin-left: -0.6rem;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    > div {
      &:last-child {
        flex-grow: 0.8;
        justify-content: flex-end;
      }
    }

    p {
      color: white;
      opacity: 0.3;
    }

    input {
      color: white;
      opacity: 0.7;
    }

    span,
    input {
      font-size: 1rem;
    }

    input,
    span,
    p {
      text-align: right;
      padding: 0 1rem 0;

      > * {
        padding: 0;
      }
    }
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const Caption = styled.div`
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 12px;
  text-indent: 2px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #0c1f38;
  border-radius: 3px;
  color: #b5bbc1;
  min-height: 232px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    background-color: transparent;
    min-height: 0;

    .select-menu-list {
      text-align: right;
      font-size: 1rem;
      line-height: 1.5rem;
      background-color: black;

      > div {
        height: 40px;
      }
    }
  }
`;

const Header = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-weight: 500;
  position: sticky;
`;

const Row = styled.div<{ isOrderBook: boolean }>`
  display: grid;
  height: 42px;
  grid-template-columns: ${(props) =>
    props.isOrderBook
      ? '140px 64px 80px 105px 84px 72px 80px 45px'
      : '140px 64px 80px 105px 88px 84px 64px 90px 45px 98px'};

  > * {
    display: flex;
    align-items: stretch;
    flex-direction: column;
    justify-content: center;
    font-size: 0.8rem;
    padding: 0 0.3rem;

    &:first-child {
      padding-left: 0.6rem;
    }

    ${({ isOrderBook }) =>
      !isOrderBook &&
      `
        &:last-child {
          padding-left: 12px;
        }

        &:nth-last-child(2) {
          padding-right: 12px;
          padding-left: 12px;
        }
      `}
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    flex-direction: column;
    padding: 0.75rem 1rem 1rem;
    height: auto;
    background-color: ${darkerBlackPearl};
    border-radius: 0.25rem;

    > * {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 0;
      margin: 0.25rem 0;

      &:first-child {
        padding: 0;
      }
    }

    &:first-child {
      margin-top: 1rem;
    }
  }
`;

const Body = styled.div`
  ${Row}:nth-child(odd) {
    &:hover {
      background-color: rgba(0, 0, 0, 0.075);
    }
  }
  ${Row}:nth-child(even) {
    background-color: rgba(255, 255, 255, 0.02);
    &:hover {
      background-color: rgba(255, 255, 255, 0.035);
    }
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: grid;
    gap: 1rem;

    ${Row}:nth-child(odd) {
      background-color: ${darkerBlackPearl};
      &:hover {
        background-color: ${darkerBlackPearl};
      }
    }

    ${Row}:nth-child(even) {
      background-color: ${darkerBlackPearl};
      &:hover {
        background-color: ${darkerBlackPearl};
      }
    }

    .select-value-container {
      justify-content: flex-end;
      padding: 0;
      align-items: center;
      font-size: 1rem;
    }

    .select-dropdown-indicator {
      padding: 0;
      align-items: center;
    }

    .mobile-align-items-start {
      align-items: flex-start;
    }
  }
`;

const HedgeBadge = styled.span.attrs({
  children: 'H',
  title: 'Hedge leg',
})`
  position: absolute;
  left: 0px;
  background-color: ${paradigmLightBlue};
  color: white;
  border-radius: 3px;
  height: 12px;
  width: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 8px;
  font-weight: bold;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const Label = styled.div<{ disabled?: boolean }>`
  font-size: 1rem;
  line-height: 2rem;
  display: none;
  color: ${blueMana};
  opacity: ${(props) => (props.disabled === true ? 0.5 : 1)};

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;

const Divider = styled(DefaultDivider)`
  display: none;
  width: 100%;
  margin: 1rem 0;
  background-color: transparent;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
  }
`;
